import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { get } from 'lodash'

import ApiService from '../../../../services/ApiService'
import CollapsibleSectionHeader from '../components/CollapsibleSectionHeader'
import { OrderCoreSchemaType } from '../orderCore.schema'
import { FormGrid, FormGroup, Select } from '../styles/shared'
import { freightModeLabel, isFreightModeCanonical } from '../../../../constants/freightModes'
import { Input, Label } from './Details.style'

interface ShippingProps {
  /** valor do frete: aço carrega número, fashion carrega dígitos de centavos */
  valueSlot?: React.ReactNode
  carrierSlot?: React.ReactNode
  deadlineSlot?: React.ReactNode
  children?: React.ReactNode
}

const Shipping: React.FC<ShippingProps> = ({ valueSlot, carrierSlot, deadlineSlot, children }) => {
  const [isOpen, setIsOpen] = useState(true)
  const { register, setValue, watch, formState: { errors } } = useFormContext<OrderCoreSchemaType>()

  const [shippingTypes, setShippingTypes] = useState<{ id: number; name: string }[]>([])

  useEffect(() => {
    let active = true
    ApiService.steelOrders.getShippingTypes({ page: 1, limit: 100 })
      .then((response) => {
        if (!active) return
        const data = Array.isArray(response.data) ? response.data : (response.data?.data ?? [])
        setShippingTypes(data)
      })
      .catch(() => {
        // Sem a lista, o select mantém só a opção já gravada no pedido.
        if (active) setShippingTypes([])
      })
    return () => { active = false }
  }, [])

  const shippingTypeId = watch('shippingTypeId')
  const shippingTypeName = watch('shippingTypeName')

  // Só as seis modalidades da NF-e ficam escolhíveis: nome livre herdado não volta a
  // circular. O valor já gravado no pedido entra como opção extra, senão editar um
  // pedido antigo apagaria o frete dele em silêncio.
  const shippingTypeOptions = useMemo(() => {
    const canonicas = shippingTypes.filter((t) => isFreightModeCanonical(t.name))
    if (!shippingTypeId || canonicas.some((t) => Number(t.id) === Number(shippingTypeId))) {
      return canonicas
    }
    const atual = shippingTypes.find((t) => Number(t.id) === Number(shippingTypeId))
    return [...canonicas, { id: Number(shippingTypeId), name: String(atual?.name ?? shippingTypeName ?? '') }]
  }, [shippingTypes, shippingTypeId, shippingTypeName])

  const handleSelectShippingType = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value ? Number(e.target.value) : undefined
    const chosen = shippingTypeOptions.find((t) => Number(t.id) === id)
    setValue('shippingTypeId', id, { shouldDirty: true })
    setValue('shippingTypeName', chosen?.name ?? '', { shouldDirty: true })
  }, [setValue, shippingTypeOptions])

  return (
    <>
      <CollapsibleSectionHeader
        title="Frete"
        isOpen={isOpen}
        onToggle={() => setIsOpen((v) => !v)}
      />

      {isOpen && (
        <>
          <FormGrid>
            <FormGroup>
              <Label htmlFor="shippingTypeId">Tipo de Frete</Label>
              <Select
                id="shippingTypeId"
                value={shippingTypeId ? String(shippingTypeId) : ''}
                onChange={handleSelectShippingType}
                hasError={!!get(errors, 'shippingTypeId')}
              >
                <option value="">Selecione</option>
                {shippingTypeOptions.map((opt) => (
                  <option key={opt.id} value={String(opt.id)}>
                    {freightModeLabel(opt.name)}
                  </option>
                ))}
              </Select>
            </FormGroup>

            {valueSlot}
            {carrierSlot}

            <FormGroup>
              <Label htmlFor="trackingCode">Código de rastreio</Label>
              <Input id="trackingCode" {...register('trackingCode')} placeholder="Digite" />
            </FormGroup>
          </FormGrid>

          <FormGrid columns={4}>
            <FormGroup>
              <Label htmlFor="orderDate">
                Data do Pedido <span style={{ color: '#e53e3e', marginLeft: '4px' }}>*</span>
              </Label>
              <Input id="orderDate" type="date" {...register('orderDate')} hasError={!!get(errors, 'orderDate')} />
            </FormGroup>

            {deadlineSlot}

            <FormGroup>
              <Label htmlFor="deliveryDate">
                Previsão de entrega <span style={{ color: '#e53e3e', marginLeft: '4px' }}>*</span>
              </Label>
              <Input id="deliveryDate" type="date" {...register('deliveryDate')} hasError={!!get(errors, 'deliveryDate')} />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="billingDate">Previsão de faturamento</Label>
              <Input id="billingDate" type="date" {...register('billingDate')} hasError={!!get(errors, 'billingDate')} />
            </FormGroup>
          </FormGrid>

          {children}
        </>
      )}
    </>
  )
}

export default Shipping
